/**
 * Sol Invictus - Main Application
 * High-fidelity 3D visualization of helios-proximal energy architecture
 * MOBILE OPTIMIZED - Detects device and adjusts quality automatically
 */

import { Suspense, useEffect, useRef, useCallback, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { Stats, AdaptiveDpr, AdaptiveEvents } from '@react-three/drei';
import {
  EffectComposer,
  Bloom,
  Vignette,
  ChromaticAberration,
} from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';

// R3F Components
import { SunCore } from './components/r3f/SunCore';
import { SwarmRing } from './components/r3f/SwarmRing';
import { LaserStream } from './components/r3f/LaserStream';
import { LDRParticles } from './components/r3f/LDRParticles';
import { EarthTarget } from './components/r3f/EarthTarget';
import { Starfield, MilkyWayPlane } from './components/r3f/Starfield';
import { GEOReceiverStation } from './components/r3f/SpacecraftModels';
// NOTE: MercuryRelayStation removed - now using IndustrialMercury with integrated OrbitalRelayHub
import { IndustrialMercury } from './components/r3f/IndustrialMercury';
import { MiningTransports } from './components/r3f/MiningTransports';
import { EAHPConstructionBase } from './components/r3f/EAHPConstructionBase';
import { InterplanetaryStarships } from './components/r3f/InterplanetaryStarships';
import { Moon } from './components/r3f/Moon';
import { CameraController } from './components/r3f/CameraController';
import { MusicVideoMode, MusicVideoOverlay, HeroStarship, LaunchStarship, ReturnStarship, CockpitInterior } from './components/r3f/MusicVideoMode';

// UI Components
import { HUD } from './components/ui/HUD';
import { InspectorModal } from './components/ui/InspectorModal';

// Store
import { useStore, INSPECTABLE_OBJECTS } from './store/useStore';

// Performance utilities
import { usePerformanceSettings, PerformanceSettings } from './utils/mobileDetect';

// Scene positions - PAPER-ACCURATE PROPORTIONS
// Ratios: EAHP 0.1 AU : Mercury 0.4 AU : Earth 1.0 AU = 1:4:10
// Base unit: 8 units = 0.1 AU
const POSITIONS = {
  sun: [-30, 0, 0] as [number, number, number],
  eahpOrbit: 8,  // 0.1 AU = 8 units (baseline)
  eahpBase: [-30 + 8 * 0.7, 1, 8 * 0.7] as [number, number, number], // Construction station
  mercury: [-2, 0, 15] as [number, number, number],  // 0.4 AU = ~32 units from sun
  earth: [50, 0, 0] as [number, number, number],     // 1.0 AU = 80 units from sun
  moon: [50, 0, -5] as [number, number, number],     // Lunar orbit - visible offset from Earth
  geoReceiver: [50 + 4, 3, 5] as [number, number, number], // GEO orbit, clear LOS
};

// CINEMATIC SCALES - Adjusted for impressive visual composition
const SCALES = {
  sun: 4,        // Dominant visual anchor
  mercury: 1.8,  // Industrial hub - LARGER than before, prominent
  earth: 1.2,    // Blue marble
};

// Main scene content - MOBILE OPTIMIZED
function Scene({ perf }: { perf: PerformanceSettings }) {
  const { setInspectedObject } = useStore();

  // Starship tracking refs for camera system
  const starshipPositionsRef = useRef<THREE.Vector3[]>([]);
  const starshipDirectionsRef = useRef<THREE.Vector3[]>([]);

  // Callback to update starship tracking data
  const handleShipUpdate = useCallback((index: number, position: THREE.Vector3, direction: THREE.Vector3) => {
    starshipPositionsRef.current[index] = position;
    starshipDirectionsRef.current[index] = direction;
  }, []);

  // Click handlers for interactive objects
  const handleMercuryRelayClick = () => {
    setInspectedObject(INSPECTABLE_OBJECTS.mercuryRelay);
  };

  const handleGEOReceiverClick = () => {
    setInspectedObject(INSPECTABLE_OBJECTS.geoReceiver);
  };

  const handleSwarmClick = () => {
    setInspectedObject(INSPECTABLE_OBJECTS.eahpSwarm);
  };

  // Optimized counts based on device
  const swarmCount = perf.swarmCount;
  const starfieldCount = perf.starfieldCount;
  const particleCount = Math.round(2000 * perf.particleMultiplier);
  const miningShipCount = perf.isMobile ? 6 : 12;
  const starshipCount = perf.isMobile ? 2 : 4;

  return (
    <>
      {/* Adaptive performance helpers */}
      <AdaptiveDpr pixelated />
      <AdaptiveEvents />

      {/* Advanced Camera Controller - handles all camera modes */}
      <CameraController
        starshipPositions={starshipPositionsRef}
        starshipDirections={starshipDirectionsRef}
      />

      {/* Music Video Clip Mode - cinematic camera sequence */}
      <MusicVideoMode />

      {/* Hero Starship - syncs with clip for Mercury landing sequence */}
      <HeroStarship />

      {/* Launch Starship - visible during opening Earth launch sequence */}
      <LaunchStarship />

      {/* Cockpit Interior - visible during cockpit scene with Elon & Ham photos */}
      <CockpitInterior />

      {/* Return Starship - visible during return journey from Mercury to Earth */}
      <ReturnStarship />

      {/* Background - CINEMATIC deep space */}
      <color attach="background" args={['#010104']} />
      <Starfield count={starfieldCount} radius={450} />
      {!perf.isLowEnd && <MilkyWayPlane />}

      {/* Sun (primary light source) - DOMINANT VISUAL ANCHOR */}
      <SunCore
        position={POSITIONS.sun}
        radius={SCALES.sun}
        intensity={1.0}
      />

      {/* EAHP Satellite Swarm - Dense dust ring (CLICKABLE) */}
      <group onClick={(e) => { e.stopPropagation(); handleSwarmClick(); }}>
        <SwarmRing
          sunPosition={POSITIONS.sun}
          orbitRadius={POSITIONS.eahpOrbit}
          satelliteCount={swarmCount}
        />
      </group>

      {/* EAHP CONSTRUCTION BASE - Assembly station where mining ships arrive */}
      <EAHPConstructionBase
        position={POSITIONS.eahpBase}
        sunPosition={POSITIONS.sun}
        scale={0.2}
      />

      {/* Liquid Droplet Radiator particles - skip on low-end mobile */}
      {!perf.isLowEnd && (
        <LDRParticles
          sunPosition={POSITIONS.sun}
          orbitRadius={POSITIONS.eahpOrbit}
          particleCount={particleCount}
        />
      )}

      {/* Laser transmission network */}
      <LaserStream
        sunPosition={POSITIONS.sun}
        mercuryPosition={POSITIONS.mercury}
        earthPosition={POSITIONS.earth}
        eahpBasePosition={POSITIONS.eahpBase}
      />

      {/* Earth (target) - BLUE MARBLE */}
      <EarthTarget
        position={POSITIONS.earth}
        radius={SCALES.earth}
      />

      {/* Moon - ULTRA-REALISTIC lunar surface */}
      <Moon
        position={POSITIONS.moon}
        radius={SCALES.earth * 0.27}  // Moon is ~27% of Earth's diameter
        earthPosition={POSITIONS.earth}
      />

      {/* INDUSTRIAL MERCURY - Mining hub, ISRU base, and optical relay center
          PROMINENT industrial planetoid with dense surface infrastructure */}
      <IndustrialMercury
        position={POSITIONS.mercury}
        radius={SCALES.mercury}
        sunPosition={POSITIONS.sun}
        onClick={handleMercuryRelayClick}
      />

      {/* MINING TRANSPORT FLEET - Ships moving between Mercury and EAHP base */}
      <MiningTransports
        mercuryPosition={POSITIONS.mercury}
        sunPosition={POSITIONS.sun}
        eahpRadius={POSITIONS.eahpOrbit}
        shipCount={miningShipCount}
      />

      {/* INTERPLANETARY STARSHIPS - Initial deployment from Earth to Mercury
          Includes realistic landing animation with retrograde flip and leg deployment */}
      <InterplanetaryStarships
        earthPosition={POSITIONS.earth}
        mercuryPosition={POSITIONS.mercury}
        shipCount={starshipCount}
        onShipUpdate={handleShipUpdate}
      />

      {/* GEO RECEIVER STATION - LOS-optimized orbital position
          Clear line-of-sight to Mercury relay, no Earth occultation */}
      <GEOReceiverStation
        position={POSITIONS.geoReceiver}
        onClick={handleGEOReceiverClick}
      />

      {/* Lighting for Earth visibility */}
      <hemisphereLight
        color={0xffffff}
        groundColor={0x444444}
        intensity={0.15}
      />

      {/* Minimal ambient for visibility of non-lit objects */}
      <ambientLight intensity={0.05} />

      {/* Fill light for Earth side */}
      <directionalLight
        position={[50, 20, 30]}
        color={0xffffff}
        intensity={0.2}
      />
    </>
  );
}


// Post-processing effects - MOBILE OPTIMIZED
function Effects({ perf }: { perf: PerformanceSettings }) {
  // Skip all effects on ultra-low-end devices
  if (!perf.enablePostProcessing) {
    return null;
  }

  return (
    <EffectComposer multisampling={perf.isMobile ? 0 : 4}>
      {/* UNREAL BLOOM - Reduced on mobile */}
      {perf.enableBloom && (
        <Bloom
          intensity={perf.isMobile ? 1.2 : 2.0}
          luminanceThreshold={perf.isMobile ? 0.2 : 0.1}
          luminanceSmoothing={0.9}
          mipmapBlur
          radius={perf.isMobile ? 0.5 : 0.8}
        />
      )}

      {/* Cinematic vignette - always on, it's cheap */}
      <Vignette
        offset={0.35}
        darkness={0.7}
        blendFunction={BlendFunction.NORMAL}
      />

      {/* Chromatic aberration - skip on mobile */}
      {!perf.isMobile && (
        <ChromaticAberration
          offset={[0.001, 0.001]}
          blendFunction={BlendFunction.NORMAL}
        />
      )}
    </EffectComposer>
  );
}

// Loading screen
function LoadingScreen() {
  const { isLoading, loadingProgress, loadingStatus, setLoading } = useStore();

  useEffect(() => {
    // Simulate loading progress
    const steps = [
      { progress: 10, status: 'Initializing scene...' },
      { progress: 30, status: 'Loading star catalog...' },
      { progress: 50, status: 'Generating EAHP swarm...' },
      { progress: 70, status: 'Setting up laser network...' },
      { progress: 90, status: 'Applying post-processing...' },
      { progress: 100, status: 'Ready!' },
    ];

    let i = 0;
    const interval = setInterval(() => {
      if (i < steps.length) {
        setLoading(true, steps[i].progress, steps[i].status);
        i++;
      } else {
        setLoading(false, 100, 'Complete');
        clearInterval(interval);
      }
    }, 400);

    return () => clearInterval(interval);
  }, [setLoading]);

  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col items-center justify-center">
      <div className="text-center">
        {/* SpaceX Logo */}
        <h1 className="text-5xl font-bold text-white mb-1 tracking-wider">
          SPACE<span className="text-gray-500">X</span>
        </h1>
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-px w-12 bg-gray-600" />
          <h2 className="text-2xl font-bold text-orange-500 tracking-wider">
            SOL INVICTUS
          </h2>
          <div className="h-px w-12 bg-gray-600" />
        </div>
        <p className="text-gray-500 text-sm mb-8">
          STARSHIP HELIOS-PROXIMAL ENERGY ARCHITECTURE
        </p>

        <div className="w-64 h-1 bg-gray-800 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-gradient-to-r from-white to-gray-400 transition-all duration-300"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>

        <p className="text-gray-400 text-sm font-mono">
          {loadingStatus}
        </p>
      </div>
    </div>
  );
}

// Main App component - MOBILE OPTIMIZED
export default function App() {
  // Get performance settings based on device
  const perf = usePerformanceSettings();

  return (
    <div className="w-screen h-screen bg-black overflow-hidden">
      {/* Loading screen */}
      <LoadingScreen />

      {/* 3D Canvas - OPTIMIZED FOR MOBILE */}
      <Canvas
        gl={{
          antialias: !perf.isMobile, // Disable AA on mobile
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 0.8,
          powerPreference: 'high-performance',
          precision: perf.isMobile ? 'mediump' : 'highp',
          alpha: false,
          stencil: false,
          depth: true,
        }}
        dpr={perf.dpr}
        performance={{ min: 0.5 }}
        frameloop="always"
      >
        <Suspense fallback={null}>
          <Scene perf={perf} />
          <Effects perf={perf} />
        </Suspense>
      </Canvas>

      {/* HUD Overlay */}
      <HUD />

      {/* Inspector Modal - Click-to-view overlay */}
      <InspectorModal />

      {/* Music Video Clip Overlay */}
      <MusicVideoOverlay />

      {/* Performance stats (dev only) */}
      {import.meta.env.DEV && <Stats className="!absolute !top-auto !bottom-20 !left-4" />}

      {/* Mobile performance indicator (dev only) */}
      {import.meta.env.DEV && perf.isMobile && (
        <div className="fixed bottom-4 right-4 bg-yellow-500/80 text-black px-2 py-1 rounded text-xs font-mono">
          MOBILE MODE {perf.isLowEnd ? '(LOW-END)' : ''}
        </div>
      )}
    </div>
  );
}
